// Helper utility for handling lesson detail audio URLs
// This follows the new structure: /audio/[pathway]/[lesson]/[category]/[english-filename].mp3

export interface AudioUrlInfo {
  pathwayId: string;
  lessonId: string;
  category: string;
  englishText: string;
}

/**
 * Parse an audio URL to extract its components
 * @param audioUrl - The audio URL to parse
 * @returns AudioUrlInfo object or null if URL doesn't match expected format
 */
export function parseAudioUrl(audioUrl: string): AudioUrlInfo | null {
  // Expected format: /audio/[pathway]/[lesson]/[category]/[english-filename].mp3
  const match = audioUrl.match(/^\/audio\/([^\/]+)\/([^\/]+)\/([^\/]+)\/([^\/]+)\.mp3$/);
  
  if (!match) {
    return null;
  }
  
  const [, pathwayId, lessonId, category, filename] = match;
  
  // Convert filename back to readable English text
  const englishText = filename
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
  
  return {
    pathwayId,
    lessonId,
    category,
    englishText
  };
}

/**
 * Generate a new audio URL following the correct structure
 * @param pathwayId - The pathway ID
 * @param lessonId - The lesson ID
 * @param englishText - The English text to use for the filename
 * @param category - The category (wordbank, dialogue, phrases, etc.)
 * @returns Properly formatted audio URL
 */
export function generateAudioUrl(
  pathwayId: string, 
  lessonId: string, 
  englishText: string, 
  category: string = 'wordbank'
): string {
  // Normalize English text for filename
  const normalized = englishText.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-|-$/g, '');
  
  return `/audio/${pathwayId}/${lessonId}/${category}/${normalized}.mp3`;
}

/**
 * Check if an audio URL follows the correct structure
 * @param audioUrl - The audio URL to validate
 * @returns True if URL follows correct structure
 */
export function isValidAudioUrl(audioUrl: string): boolean {
  return parseAudioUrl(audioUrl) !== null;
}

/**
 * Update wordbank entries with correct audio URLs
 * @param wordBank - Array of wordbank entries
 * @param pathwayId - The pathway ID
 * @param lessonId - The lesson ID
 * @returns Updated wordbank array
 */
export function updateWordBankAudioUrls(
  wordBank: any[], 
  pathwayId: string, 
  lessonId: string
): any[] {
  return wordBank.map(word => {
    if (word.english || word.translation) {
      const englishText = word.english || word.translation;
      return {
        ...word,
        audioUrl: generateAudioUrl(pathwayId, lessonId, englishText, 'wordbank')
      };
    }
    return word;
  });
}

/**
 * Update lesson content with correct audio URLs
 * @param content - The lesson content object
 * @param pathwayId - The pathway ID
 * @param lessonId - The lesson ID
 * @returns Updated content object
 */
export function updateLessonContentAudioUrls(
  content: any, 
  pathwayId: string, 
  lessonId: string
): any {
  const updatedContent = { ...content };
  
  // Update wordBank entries
  if (updatedContent.wordBank && Array.isArray(updatedContent.wordBank)) {
    updatedContent.wordBank = updateWordBankAudioUrls(updatedContent.wordBank, pathwayId, lessonId);
  }
  
  // Update dialogue entries in scenes
  if (updatedContent.scenes && Array.isArray(updatedContent.scenes)) {
    updatedContent.scenes = updatedContent.scenes.map((scene: any) => {
      if (scene.dialogue && Array.isArray(scene.dialogue)) {
        scene.dialogue = scene.dialogue.map((dialogue: any) => {
          if (dialogue.english || dialogue.translation) {
            const englishText = dialogue.english || dialogue.translation;
            return {
              ...dialogue,
              audioUrl: generateAudioUrl(pathwayId, lessonId, englishText, 'dialogue')
            };
          }
          return dialogue;
        });
      }
      return scene;
    });
  }
  
  // Update phrases if they exist
  if (updatedContent.phrases && Array.isArray(updatedContent.phrases)) {
    updatedContent.phrases = updatedContent.phrases.map((phrase: any) => {
      if (phrase.english || phrase.translation) {
        const englishText = phrase.english || phrase.translation;
        return {
          ...phrase,
          audioUrl: generateAudioUrl(pathwayId, lessonId, englishText, 'phrases')
        };
      }
      return phrase;
    });
  }
  
  return updatedContent;
}

/**
 * Get the display text for an audio URL (for debugging/display purposes)
 * @param audioUrl - The audio URL
 * @returns Human-readable text describing the audio
 */
export function getAudioUrlDisplayText(audioUrl: string): string {
  const parsed = parseAudioUrl(audioUrl);
  if (!parsed) {
    return 'Invalid audio URL format';
  }
  
  return `${parsed.category}: ${parsed.englishText}`;
}

/**
 * Validate and fix audio URLs in a lesson object
 * @param lesson - The lesson object to validate
 * @param pathwayId - The pathway ID
 * @returns Updated lesson object with fixed audio URLs
 */
export function validateAndFixLessonAudioUrls(lesson: any, pathwayId: string): any {
  const updatedLesson = { ...lesson };
  
  // Fix main wordBank entries
  if (updatedLesson.wordBank && Array.isArray(updatedLesson.wordBank)) {
    updatedLesson.wordBank = updateWordBankAudioUrls(updatedLesson.wordBank, pathwayId, lesson.id);
  }
  
  // Fix lesson content if it exists
  if (updatedLesson.content && typeof updatedLesson.content === 'string') {
    try {
      const content = JSON.parse(updatedLesson.content);
      const updatedContent = updateLessonContentAudioUrls(content, pathwayId, lesson.id);
      updatedLesson.content = JSON.stringify(updatedContent);
    } catch (error) {
      console.warn('Could not parse lesson content:', error);
    }
  }
  
  // Fix phrases array if it exists
  if (updatedLesson.phrases && Array.isArray(updatedLesson.phrases)) {
    updatedLesson.phrases = updatedLesson.phrases.map((phrase: any) => {
      if (phrase.english || phrase.translation) {
        const englishText = phrase.english || phrase.translation;
        return {
          ...phrase,
          audioUrl: generateAudioUrl(pathwayId, lesson.id, englishText, 'phrases')
        };
      }
      return phrase;
    });
  }
  
  return updatedLesson;
}
